import { config } from '@iheartjane/config';
import { PageContextEnum, Placement } from '@iheartjane/dm-sdk';
import type { SmartSort, SmartSortProduct } from '@iheartjane/dm-sdk';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { useMemo } from 'react';

import {
  useFetchCustomMultiPlacements,
  useRecommendedSort,
} from '@jane/dm/internal';
import { useSearchContext } from '@jane/search/hooks';
import type { AlgoliaProduct } from '@jane/search/types';
import {
  buildIndex,
  buildSearchFilter,
  ensureRecommendedDefaultSort,
  mapCurrentSortToSearchSort,
  parseKindFromStaticFilters,
} from '@jane/search/util';
import { useMenu } from '@jane/shared-ecomm/providers';
import {
  getMenuSearchEventDetails,
  useTrackSearch,
} from '@jane/shared-ecomm/tracking';
import { calculateGridColumns } from '@jane/shared-ecomm/util';

interface UseMyHighProps {
  disableAds?: boolean;
  enableMidGridRow?: boolean;
  maxProducts?: number;
  productsPerPage: number;
  staticFilters?: string;
}

export interface UseMyHighResponse<
  ProductShape extends object = AlgoliaProduct,
> {
  dynamicPlacement?: SmartSort<AlgoliaProduct>;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  indexName: string;
  isLoading: boolean;
  numColumns: number | null;
  numHits: number;
  products: SmartSortProduct<ProductShape>[];
}

export const useMyHigh = ({
  disableAds = false,
  enableMidGridRow = false,
  maxProducts,
  productsPerPage,
  staticFilters,
}: UseMyHighProps): UseMyHighResponse => {
  const { searchState } = useSearchContext();
  const midGridAdRow = useFlag(FLAGS.c3MidGridAdRow);

  const { listView, store } = useMenu();

  const searchFilter = buildSearchFilter({
    searchState,
    staticFilters,
  });

  const productGrid = (
    document.getElementById('shadow-host')?.shadowRoot || document
  ).getElementById('empty-menu-product-grid');

  const numColumns = calculateGridColumns(
    productGrid?.getBoundingClientRect().width
  );

  const columnsForQuery = listView ? 1 : numColumns;

  const defaultSort = ensureRecommendedDefaultSort(searchState.currentSort);

  const searchSort = mapCurrentSortToSearchSort(
    defaultSort ?? searchState.currentSort
  );

  const filterContext = useMemo(
    () =>
      enableMidGridRow ? parseKindFromStaticFilters(staticFilters) : undefined,
    [staticFilters, enableMidGridRow]
  );

  const { placementViewModels, isLoading } = useFetchCustomMultiPlacements({
    storeId: Number(store.id),
    numColumns: columnsForQuery ?? undefined,
    placements: [
      {
        placement: Placement.MenuInlineTable,
        disableAds,
        maxProducts,
        pageSize: productsPerPage,
        searchFilter,
        searchQuery: searchState?.searchText ?? undefined,
        searchSort,
      },
      ...(midGridAdRow && enableMidGridRow
        ? [
            {
              placement: Placement.MenuDynamicRow as const,
              filterContext,
              pageContext: PageContextEnum.Midgrid,
            },
          ]
        : []),
    ],
    enabled: maxProducts !== 0,
  });

  const recommendedSortResponse = useRecommendedSort({
    disableAds,
    enabled: maxProducts === 0,
    maxProducts,
    numColumns: columnsForQuery ?? undefined,
    pageSize: productsPerPage,
    searchAttributes: ['*'],
    searchFilter,
    searchQuery: searchState?.searchText ?? undefined,
    searchSort,
    storeId: Number(store.id),
    searchFacets: [],
  });

  const { isPlacement, fetchNextPage, hasNextPage, placement } =
    placementViewModels[0] ?? {};

  const { isPlacement: isDynamicPlacement, placement: dynamicPlacement } =
    placementViewModels?.length > 1 ? placementViewModels[1] : {};

  const searchIndex = buildIndex(
    config.algoliaEnv,
    defaultSort ?? searchState.currentSort
  );

  useTrackSearch({
    event: getMenuSearchEventDetails({
      store,
      view: listView ? 'list' : 'grid',
    }),
    indexName: searchIndex,
    numHits: isPlacement ? placement.totalProducts : 0,
    searchState,
    searching: isLoading,
  });

  return {
    dynamicPlacement:
      isDynamicPlacement && dynamicPlacement ? dynamicPlacement : undefined,
    fetchNextPage,
    hasNextPage,
    indexName: searchIndex,
    isLoading,
    numColumns,
    numHits: isPlacement
      ? placement.totalProducts
      : (recommendedSortResponse.numHits ?? 0),
    products: isPlacement ? placement.products : [],
  };
};
